import type { FC } from "react";
import { useState } from "react";
import { Typography, Stack, IconButton, Divider } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import type { OrganisationHierarchy } from "../../../api/organisations.types";
import { useGetOrganisationHierarchy } from "../../../api/organisations";
import { useGetHasConnectedEntitiesMap } from "../../../api/tenants";
import { PAGE_PADDING_X, PageSpinner, PageTitle, PageWrapper } from "../../../shared/Page";
import { ChevronDownIcon } from "../../../shared/icons";
import { usePrivacyDocumentTitle } from "../../../shared/usePrivacyDocumentTitle";
import { TenantMenu } from "./TenantMenu/TenantMenu";

export const OrganisationStructurePage = () => {
  usePrivacyDocumentTitle("Sidebar.OrganisationTree");
  const { t } = useTranslation();
  const organisationHierarchyQuery = useGetOrganisationHierarchy();
  const hasConnectedEntitiesMapQuery = useGetHasConnectedEntitiesMap();
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());

  if (!organisationHierarchyQuery.data || !hasConnectedEntitiesMapQuery.data) return <PageSpinner />;

  const toggleExpanded = (id: string) => {
    setCollapsedIds(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const hasConnectedEntities = (tenant: OrganisationHierarchy) => {
    const hasConnections = hasConnectedEntitiesMapQuery.data.get(tenant.id);

    if (hasConnections === undefined || hasConnections) {
      return true;
    }

    return tenant.children.some(hasConnectedEntities);
  };

  return (
    <PageWrapper sx={{ px: 0 }}>
      <Stack sx={{ flexWrap: "wrap" }}>
        <PageTitle
          wrapperSx={{ px: PAGE_PADDING_X }}
          title={t("OrganisationStructure.Title")}
          subTitle={t("OrganisationStructure.SubTitle")}
        />
        <Divider sx={{ my: 4 }} />
        <Stack sx={{ mt: 2, maxWidth: 1000, px: PAGE_PADDING_X }}>
          <TenantTree
            tenant={organisationHierarchyQuery.data}
            level={0}
            collapsedIds={collapsedIds}
            onToggleExpand={toggleExpanded}
            hasConnectedEntities={hasConnectedEntities}
          />
        </Stack>
      </Stack>
    </PageWrapper>
  );
};

type TenantTreeProps = {
  tenant: OrganisationHierarchy;
  level: number;
  collapsedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  hasConnectedEntities: (tenant: OrganisationHierarchy) => boolean;
};

const TenantTree: FC<TenantTreeProps> = ({ tenant, level, collapsedIds, onToggleExpand, hasConnectedEntities }) => {
  const { t } = useTranslation();
  const isExpanded = !collapsedIds.has(tenant.id);
  const hasChildren = tenant.children && tenant.children.length > 0;

  return (
    <Stack key={tenant.id} sx={{ gap: 1 }}>
      <Stack sx={{ flex: "1", pl: level > 0 ? `${level * 2}rem` : 0 }}>
        <Stack
          data-testid={tenant.organizationName}
          direction="row"
          spacing={3}
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "-1px",
            p: 1,
            bgcolor: theme => theme.palette.background.paper,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            borderLeft: `3px solid`,
            borderLeftColor: theme => theme.palette.brandOrange["500"],
          }}
        >
          <Stack direction="row" spacing={1} sx={{ flex: "1", alignItems: "center" }}>
            <IconButton
              aria-label={t(isExpanded ? "Common.Collapse" : "Common.Expand")}
              onClick={() => onToggleExpand(tenant.id)}
              sx={{
                visibility: hasChildren ? "visible" : "hidden",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
              }}
            >
              <ChevronDownIcon size={14} />
            </IconButton>

            <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14) }}>{tenant.organizationName}</Typography>
          </Stack>

          <TenantMenu tenant={tenant} isRoot={!tenant.parentId} hasConnectedEntities={hasConnectedEntities} />
        </Stack>
      </Stack>
      {isExpanded &&
        hasChildren &&
        tenant.children.map(child => (
          <TenantTree
            key={child.id}
            tenant={child}
            level={level + 1}
            collapsedIds={collapsedIds}
            onToggleExpand={onToggleExpand}
            hasConnectedEntities={hasConnectedEntities}
          />
        ))}
    </Stack>
  );
};
